import { useState } from 'react';
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from "@mui/material/Box";

export default function AddableList({ label, items, onAdd, onRemove, disabled, isObject = false, enableNotes = false, onNoteChange, maxItems, allowEdit = false, onUpdate }) {
  const [newItem, setNewItem] = useState('');
  const [editingIndex, setEditingIndex] = useState(-1);
  const [editingValue, setEditingValue] = useState('');

  const handleAdd = () => {
    if (newItem.trim() && (!maxItems || items.length < maxItems)) {
      const value = isObject ? { name: newItem.trim(), ...(enableNotes ? { note: '' } : {}) } : newItem.trim();
      onAdd(value);
      setNewItem('');
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && newItem.trim() && (!maxItems || items.length < maxItems)) {
      event.preventDefault();
      handleAdd();
    }
  };

  const handleEdit = (index) => {
    const item = items[index];
    const value = isObject ? item.name : item;
    setEditingValue(value);
    setEditingIndex(index);
  };

  const handleSave = (index) => {
    const newName = editingValue.trim();
    if (newName) {
      onUpdate(index, newName);
      setEditingIndex(-1);
    }
  };

  const handleCancel = () => {
    setEditingIndex(-1);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          label={label}
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyDown={handleKeyDown}
          variant="filled"
          fullWidth
          disabled={disabled}
        />
        <Button variant="contained" onClick={handleAdd} disabled={disabled || !newItem.trim() || (maxItems && items.length >= maxItems)}>
          Add
        </Button>
      </Box>
      {items.length > 0 && (
        <List>
          {items.map((item, index) => (
            <ListItem
              key={index}
              sx={{ paddingRight: 12 }}
              secondaryAction={
                <>
                  {editingIndex === index ? (
                    <>
                      <IconButton edge="start" aria-label="save" onClick={() => handleSave(index)} disabled={disabled}>
                        <SaveIcon />
                      </IconButton>
                      <IconButton edge="end" aria-label="cancel" onClick={handleCancel} disabled={disabled}>
                        <CancelIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      {allowEdit && (
                        <IconButton edge="start" aria-label="edit" onClick={() => handleEdit(index)} disabled={disabled}>
                          <EditIcon />
                        </IconButton>
                      )}
                      <IconButton edge="end" aria-label="delete" onClick={() => onRemove(index)} disabled={disabled}>
                        <DeleteIcon />
                      </IconButton>
                    </>
                  )}
                </>
              }
            >
              <Box sx={{ flex: 1 }}>
                {editingIndex === index ? (
                  <TextField
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    fullWidth
                    disabled={disabled}
                    autoFocus
                    multiline={editingValue.length > 50}
                    minRows={editingValue.length > 50 ? 3 : 1}
                  />
                ) : (
                  <ListItemText primary={isObject ? item.name : item} />
                )}
                {enableNotes && (
                  <TextField
                    label="Note"
                    value={item.note || ''}
                    onChange={(e) => onNoteChange?.(index, e.target.value)}
                    fullWidth
                    disabled={disabled}
                    sx={{ mt: 1 }}
                  />
                )}
              </Box>
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
}